<template>
  <ValidationObserver ref="observer">
    <v-container>
      <v-row justify="center">
        <v-col cols="10" md="8" class="form-container">
          <v-form @submit.prevent="submitForm">
            <!-- Questionnaire Name -->
            <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.questionnaire_name')"
              rules="required" v-slot="{ errors }">
              <v-textarea v-model="formData.name"
                :label="$vuetify.lang.t('$vuetify.maira_questionnaire.questionnaire_name')" dense class="form-field"
                clearable
                :error-messages="errors" auto-grow rows="1" />
            </ValidationProvider>

            <!-- Questions List -->
            <div v-for="(question, questionIndex) in formData.questions" :key="question.id">
              <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.question')" rules="required"
                v-slot="{ errors }">
                <v-card class="mb-4 question-card" outlined>
                  <v-card-text>
                    <!-- Question Text -->
                    <v-textarea v-model="question.question"
                      :label="$vuetify.lang.t('$vuetify.maira_questionnaire.question')" dense class="form-field"
                      clearable
                      :error-messages="errors" auto-grow rows="1" />

                    <!-- Question Type and Required Row -->
                    <v-row class="mb-4">
                      <v-col cols="6">
                        <v-select :items="questionTypes"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.select_type')" dense attach
                          v-model="question.type" @change="handleTypeChange(questionIndex)" />
                      </v-col>
                      <v-col cols="6">
                        <v-select :items="requiredOptions"
                          :label="$vuetify.lang.t('$vuetify.maira_questionnaire.required')" dense attach
                          v-model="question.required" />
                      </v-col>
                    </v-row>

                    <!-- Order Field -->
                    <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.order')"
                      rules="required|numeric|min_value:1" v-slot="{ errors }" :custom-messages="{
                        required: $vuetify.lang.t('$vuetify.maira_questionnaire.order_required_message')
                      }">
                      <v-text-field v-model.number="question.order"
                        :label="$vuetify.lang.t('$vuetify.maira_questionnaire.order')" type="number" dense
                        class="form-field" :error-messages="[...errors, question.duplicateOrderError].filter(Boolean)"
                        min="1" step="1" @keypress="allowOnlyDigits" />
                    </ValidationProvider>

                    <!-- Placeholder for text, number type -->
                    <v-textarea v-if="question.type === 'text' || question.type === 'number'"
                      v-model="question.placeholder"
                      :label="$vuetify.lang.t('$vuetify.maira_questionnaire.question_placeholder')" dense
                      clearable
                      class="form-field" auto-grow rows="1" />


                    <!-- Question Flow for text/number: free key/value pairs -->
                    <div v-if="question.type === 'text' || question.type === 'number'" class="d-flex align-center ">
                      <h6 class="title py-4">
                        {{ $vuetify.lang.t('$vuetify.maira_questionnaire.question_flow') }}
                      </h6>
                      <template>
                        <v-tooltip bottom max-width="450">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.question_flow_tooltip") }}</span><br>
                          <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.question_flow_tooltip_example")
                            }}</span>
                        </v-tooltip>
                      </template>
                    </div>

                    <div v-if="question.type === 'text' || question.type === 'number'">
                      <v-row v-for="(key, idx) in tempKeys[questionIndex]" :key="`${question.id}-logic-${idx}`"
                        align="center" class="mb-2">
                        <v-col cols="6">
                          <ValidationProvider ref="keyProviders" v-slot="{ errors }"
                            :rules="{ required: true, uniqueKey: { keys: tempKeys[questionIndex], currentIndex: idx } }"
                            :name="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')"
                            :custom-messages="{ uniqueKey: $vuetify.lang.t('$vuetify.maira_questionnaire.duplicate_key') }">
                            <v-combobox
                              v-model="tempKeys[questionIndex][idx]"
                              :items="getSpecialAnswerOptions(question.type, questionIndex, idx)"
                              :label="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')"
                              dense
                              attach
                              outlined
                              :append-icon="getSpecialAnswerOptions(question.type, questionIndex, idx).length > 0 ? '$dropdown' : ''"
                              @update:search-input="onComboboxSearchInput($event, questionIndex, idx)"
                              @change="updateNextLogic(questionIndex, idx)"
                              @keypress="question.type === 'number' ? allowOnlyNumbersOrSpecial($event, tempKeys[questionIndex][idx]) : null"
                              @keydown="preventSpecialValueEdit($event, questionIndex, idx)"
                              :error-messages="errors"
                            >
                              <template v-slot:item="{ item }">
                                <div
                                  class="d-flex align-center"
                                  style="width: 100%; cursor: pointer;"
                                  @click.stop="toggleValue(questionIndex, idx, item)"
                                >
                                  <v-icon class="mr-2">
                                    {{
                                      tempKeys[questionIndex][idx] === item
                                        ? 'mdi-checkbox-marked'
                                        : 'mdi-checkbox-blank-outline'
                                    }}
                                  </v-icon>

                                  <span class="flex-grow-1">{{ item }}</span>

                                  <v-tooltip bottom max-width="300">
                                    <template v-slot:activator="{ on }">
                                      <span v-on="on" class="info-icon">i</span>
                                    </template>

                                    <span v-if="question.type === 'text'">
                                      {{ $vuetify.lang.t('$vuetify.maira_questionnaire.any_text_tooltip') }}
                                    </span>
                                    <span v-else>
                                      {{ $vuetify.lang.t('$vuetify.maira_questionnaire.any_number_tooltip') }}
                                    </span>
                                  </v-tooltip>
                                </div>
                              </template>
                            </v-combobox>
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="5">
                          <ValidationProvider ref="valueProviders" v-slot="{ errors }"
                            :name="$vuetify.lang.t('$vuetify.maira_questionnaire.new_question')" rules="required">
                            <v-select :items="getNextQuestionOptions(questionIndex)"
                              v-model="tempValues[questionIndex][idx]"
                              :label="$vuetify.lang.t('$vuetify.maira_questionnaire.new_question')" dense outlined attach
                              @change="updateNextLogic(questionIndex, idx)" :error-messages="errors" />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="1" class="d-flex flex-column justify-start pt-0 pb-8">
                          <v-btn icon small color="error" @click="removeLogic(questionIndex, idx)">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-btn small color="primary" outlined @click="addLogic(questionIndex)">
                        <v-icon left small>mdi-plus</v-icon>{{
                          $vuetify.lang.t('$vuetify.maira_questionnaire.add_mapping') }}
                      </v-btn>
                    </div>
                    <!-- Options for radio/checkbox -->
                    <template v-if="question.type === 'radio' || question.type === 'checkbox'">
                      <div v-for="(option, optionIndex) in question.options"
                        :key="`${question.id}-option-${optionIndex}`">
                        <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.option')"
                          rules="required" v-slot="{ errors }">
                          <v-row align="center" class="option-row">
                            <v-col cols="1" class="option-icon">
                              <v-icon v-if="question.type === 'radio'">mdi-radiobox-blank</v-icon>
                              <v-checkbox v-else disabled />
                            </v-col>
                            <v-col cols="9">
                              <v-textarea v-model="question.options[optionIndex]"
                                :label="`${$vuetify.lang.t('$vuetify.maira_questionnaire.option')} ${optionIndex + 1}`"
                                clearable
                                dense class="option-field" :error-messages="errors" auto-grow rows="1" />
                            </v-col>
                            <v-col cols="2" class="text-right">
                              <v-btn icon @click="removeOption(questionIndex, optionIndex)"
                                :disabled="question.options.length <= 1">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </v-col>
                          </v-row>
                        </ValidationProvider>
                      </div>
                      <v-alert v-if="question.optionError" type="error" dense class="mt-2">
                        {{ question.optionError }}
                      </v-alert>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn text color="primary" outlined @click="addOption(questionIndex)" v-on="on" class="mt-2">
                            <v-icon left>mdi-plus-circle-outline</v-icon>
                            {{ $vuetify.lang.t("$vuetify.maira_questionnaire.add_option") }}
                          </v-btn>
                        </template>
                        <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.add_option") }}</span>
                      </v-tooltip>
                    </template>
                    <!-- Question Flow for text/number: free radio/checkbox pairs -->
                    <div v-if="question.type === 'radio' || question.type === 'checkbox'"
                      class="d-flex align-center  pb-4  pt-8">
                      <h6 class=" title">
                        {{ $vuetify.lang.t('$vuetify.maira_questionnaire.question_flow') }}
                      </h6>
                      <template>
                        <v-tooltip bottom max-width="450">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.question_flow_tooltip") }}</span><br>
                          <span>{{ $vuetify.lang.t("$vuetify.maira_questionnaire.question_flow_tooltip_example")
                            }}</span>
                        </v-tooltip>
                      </template>
                    </div>

                    <div v-if="question.type === 'radio' || question.type === 'checkbox'">
                      <v-row v-for="(key, idx) in tempKeys[questionIndex]" :key="`${question.id}-logic-${idx}`"
                        align="center" class="mb-2">
                        <v-col cols="6" v-if="question.type === 'checkbox'">
                          <ValidationProvider v-slot="{ errors }"
                            :name="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')"
                            :rules="{ uniqueArraySelection: { allSelections: tempKeys[questionIndex], currentIndex: idx } }"
                            :custom-messages="{ uniqueArraySelection: $vuetify.lang.t('$vuetify.maira_questionnaire.duplicate_key') }">
                            <v-combobox
                              :items="getAvailableOptions(questionIndex, idx, question.options)"
                              v-model="tempKeys[questionIndex][idx]"
                              :label="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')"
                              dense
                              outlined
                              multiple
                              chips
                              clearable
                              small-chips
                              :error-messages="errors"
                              @change="updateNextLogic(questionIndex, idx)"
                              class="custom-combobox"
                            />

                          </ValidationProvider>
                        </v-col>

                        <v-col cols="6" v-if="question.type === 'radio'">
                          <ValidationProvider ref="valueProviders" v-slot="{ errors }"
                            :name="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')" rules="required">
                            <v-select :items="getAvailableOptions(questionIndex, idx, question.options)"
                              v-model="tempKeys[questionIndex][idx]"
                              :label="$vuetify.lang.t('$vuetify.maira_questionnaire.user_answer')"
                              :error-messages="errors" dense attach outlined @change="updateNextLogic(questionIndex, idx)" />


                          </ValidationProvider>

                        </v-col>

                        <v-col cols="5">
                          <ValidationProvider ref="valueProviders" v-slot="{ errors }"
                            :name="$vuetify.lang.t('$vuetify.maira_questionnaire.new_question')" rules="required">
                            <v-select :items="getNextQuestionOptions(questionIndex)"
                              v-model="tempValues[questionIndex][idx]"
                              :label="$vuetify.lang.t('$vuetify.maira_questionnaire.new_question')" dense attach outlined
                              :error-messages="errors" @change="updateNextLogic(questionIndex, idx)" />

                          </ValidationProvider>
                        </v-col>




                        <v-col cols="1" class="d-flex flex-column justify-start pt-0 pb-8">
                          <v-btn icon small color="error" @click="removeLogic(questionIndex, idx)">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-btn small color="primary" outlined @click="addLogicRadio(questionIndex)">
                        <v-icon left small>mdi-plus</v-icon>
                        {{ $vuetify.lang.t('$vuetify.maira_questionnaire.add_mapping') }}
                      </v-btn>
                    </div>
                  </v-card-text>

                  <v-card-actions class="justify-end">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn :disabled="formData.questions.length == 1" icon
                          @click="openDeleteQuestionDialog(questionIndex)" color="red" v-on="on">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete Question</span>
                    </v-tooltip>
                  </v-card-actions>
                </v-card>
              </ValidationProvider>
            </div>

            <!-- Add Question Button -->
            <v-btn outlined text @click="addQuestion" color="primary" class="mb-4 add-question-btn">
              <v-icon left>mdi-plus</v-icon>
              {{ $vuetify.lang.t("$vuetify.maira_questionnaire.add_question") }}
            </v-btn>

            <!-- Context Template -->
            <ValidationProvider name="Context Template">
              <v-textarea v-model="formData.context_template"
                :label="$vuetify.lang.t('$vuetify.maira_questionnaire.context_template')" dense rows="6"
                class="form-field" :hint="$vuetify.lang.t('$vuetify.maira_questionnaire.context_template_hint')"
                persistent-hint outlined
                clearable
                :placeholder="$vuetify.lang.t('$vuetify.maira_questionnaire.context_template_placeholder')" 
                />
            </ValidationProvider>

            <!-- Response Instructions -->
            <ValidationProvider name="Response Instructions">
              <v-textarea v-model="formData.response_instructions"
                :label="$vuetify.lang.t('$vuetify.maira_questionnaire.response_instructions')" dense rows="6"
                class="form-field mb-10"
                clearable
                :hint="$vuetify.lang.t('$vuetify.maira_questionnaire.response_instructions_hint')" persistent-hint
                outlined />
            </ValidationProvider>

            <!-- Active Status -->
            <v-select :items="statusOptions" :label="$vuetify.lang.t('$vuetify.common.status')" dense attach
              v-model="formData.active" class="mb-4" outlined />

            <!-- One-Directional Toggle -->
            <v-checkbox v-model="formData.is_one_directional" class="mb-4">
              <template v-slot:label>
                <span class="d-flex align-center">
                  {{ $vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_label') }}
                  <v-tooltip bottom max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs" class="info-icon">i</span>
                    </template>
                    <span>{{ $vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_description') }}</span>
                  </v-tooltip>
                </span>
              </template>
            </v-checkbox>

            <!-- One-Directional Configuration Section -->
            <v-card v-if="formData.is_one_directional" class="mb-6 pa-4" outlined>
               <h4 class="title mb-4">
                  <p class="d-flex align-center">
                    <span>
                      {{ $vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_config_title') }}
                    </span>

                    <v-tooltip bottom max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-on="on"
                          v-bind="attrs"
                          class="info-icon"
                        >
                          i
                        </span>
                      </template>
                      <span>
                        {{ $vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_config_description') }}
                      </span>
                    </v-tooltip>
                  </p>
                </h4>

              <!-- Email Template -->
              <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_subject_label')" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="formData.one_directional_config.email_template.email_subject"
                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_subject_label')"
                  dense outlined class="form-field" :error-messages="errors"
                  :placeholder="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_subject_placeholder')" 
                  :hint="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_subject_hint')" 
                  persistent-hint
                  clearable
                />
              </ValidationProvider>
              <ValidationProvider :name="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_body_label')" rules="required" v-slot="{ errors }">
                <v-textarea
                  v-model="formData.one_directional_config.email_template.email_body"
                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_body_label')"
                  dense outlined class="form-field" :error-messages="errors" rows="6"
                  :placeholder="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_body_placeholder')" 
                  :hint="$vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_email_body_hint')" 
                  persistent-hint
                  clearable
                />
              </ValidationProvider>

              <!-- Senders -->
              <ValidationProvider
                :name="$vuetify.lang.t('$vuetify.maira_questionnaire.senders')"
                :rules="{ required: true, activeSender: { senders: availableSenders } }"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="formData.one_directional_config.senders"
                  :items="availableSenders"
                  item-text="text"
                  item-value="value"
                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.senders')"
                  multiple chips small-chips deletable-chips outlined clearable attach
                  :loading="isSendersLoading" :error-messages="errors"
                />
              </ValidationProvider>

              <!-- Recipients -->
              <ValidationProvider 
                :name="$vuetify.lang.t('$vuetify.maira_questionnaire.recipients')" 
                rules="required|validEmailList" 
                v-slot="{ errors }"
              >
                <v-combobox
                  v-model="formData.one_directional_config.recipients"
                  :search-input.sync="recipientSearch"
                  @blur="handleRecipientBlur"
                  @keydown.enter.prevent="addRecipientFromSearch"
                  :label="$vuetify.lang.t('$vuetify.maira_questionnaire.recipients')"
                  multiple chips small-chips deletable-chips outlined clearable
                  :hint="$vuetify.lang.t('$vuetify.maira_questionnaire.recipients_hint')" persistent-hint :error-messages="errors" append-icon=""
                />
              </ValidationProvider>

              <!-- Delivery Mode -->
              <v-select
                v-model="formData.one_directional_config.delivery_mode"
                :items="Object.values(this.deliveryModes)"
                :label="$vuetify.lang.t('$vuetify.maira_questionnaire.delivery_mode')"
                dense attach outlined class="mt-4"
              />

              <!-- Schedules (Conditional) -->
              <div v-if="formData.one_directional_config.delivery_mode === deliveryModes.SCHEDULED">
                <!-- <h5 class="subtitle-1 mt-4">
                  {{ $vuetify.lang.t('$vuetify.maira_questionnaire.schedules') }}
                </h5> -->
                <h6 class="title">
                  <p class="d-flex align-center">
                    <span>
                      {{ $vuetify.lang.t('$vuetify.maira_questionnaire.schedules') }}
                    </span>

                    <v-tooltip bottom max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-on="on"
                          v-bind="attrs"
                          class="info-icon"
                        >
                          i
                        </span>
                      </template>
                      <span>
                        {{ $vuetify.lang.t('$vuetify.maira_questionnaire.one_directional_schedule_hint') }}
                      </span>
                    </v-tooltip>
                  </p>
                </h6>
                <!-- <v-alert
                  v-if="scheduleOverlapError"
                  type="error"
                  dense
                  outlined
                  class="mb-4"
                >
                  {{ scheduleOverlapError }}
                </v-alert> -->
                <v-card
                  v-for="(schedule, index) in formData.one_directional_config.schedules"
                  :key="index"
                  class="mb-4 pa-3"
                  outlined
                >
                  <ValidationProvider 
                    :name="$vuetify.lang.t('$vuetify.maira_questionnaire.window_start')" 
                    rules="required|timeFormat" 
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="schedule.window_start"
                      type="time"
                      :label="$vuetify.lang.t('$vuetify.maira_questionnaire.window_start')"
                      placeholder="HH:MM"
                      dense
                      outlined
                      :error-messages="errors"
                      clearable
                    >
                      <template v-slot:append>
                        <v-tooltip bottom max-width="350">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon" style="margin-top: 3px;">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.maira_questionnaire.window_start_hint') }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </ValidationProvider>

                  <ValidationProvider 
                    :name="$vuetify.lang.t('$vuetify.maira_questionnaire.window_end')" 
                    :rules="{
                      required: true,
                      timeFormat: true,
                      startEndNotEqual: { start: schedule.window_start } 
                    }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="schedule.window_end"
                      type="time"
                      :label="$vuetify.lang.t('$vuetify.maira_questionnaire.window_end')"
                      placeholder="HH:MM"
                      dense
                      outlined
                      :error-messages="errors"
                      clearable
                    >
                      <template v-slot:append>
                        <v-tooltip bottom max-width="350">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon" style="margin-top: 3px;">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.maira_questionnaire.window_end_hint') }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </ValidationProvider>

                  <ValidationProvider 
                    :name="$vuetify.lang.t('$vuetify.maira_questionnaire.delivery_time')" 
                    :rules="{
                      required: true,
                      timeFormat: true,
                      circularSchedule: { start: schedule.window_start, end: schedule.window_end }
                    }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="schedule.delivery_time"
                      type="time"
                      :label="$vuetify.lang.t('$vuetify.maira_questionnaire.delivery_time')"
                      placeholder="HH:MM"
                      dense
                      outlined
                      :error-messages="errors"
                      clearable
                    >
                      <template v-slot:append>
                        <v-tooltip bottom max-width="350">
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="info-icon" style="margin-top: 3px;">i</span>
                          </template>
                          <span>{{ $vuetify.lang.t('$vuetify.maira_questionnaire.delivery_time_hint') }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </ValidationProvider>

                  <v-row justify="end">
                    <v-btn icon color="error" :ripple="false" @click="removeSchedule(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-row>
                </v-card>
                <v-btn small color="primary" outlined @click="addSchedule">
                  <v-icon left small>mdi-plus</v-icon>
                  {{ $vuetify.lang.t('$vuetify.maira_questionnaire.add_schedule') }}
                </v-btn>
              </div>
            </v-card>

            <!-- Form Actions -->
            <v-card-actions class="form-actions">
              <v-btn color="error" outlined @click="cancelQuestionnaire" class="cancel-btn">
                {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
              </v-btn>
              <v-spacer />
              <v-btn type="submit" color="primary" :loading="isLoading">
                {{ mode === 'edit' ? $vuetify.lang.t("$vuetify.common.update_btn") :
                  $vuetify.lang.t("$vuetify.common.create") }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400">
      <v-card>
        <v-card-title class="primary white--text py-4">
          {{ $vuetify.lang.t("$vuetify.maira_questionnaire.confirm_delete_question") }}
        </v-card-title>
        <v-card-text class="pa-4 mt-2 subtitle-1">
          {{ deleteDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" outlined @click="closeDeleteDialog">
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>
          <v-btn color="primary" @click="confirmDelete">
            {{ $vuetify.lang.t("$vuetify.common.confirm_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel Confirmation Dialog -->
    <v-dialog v-model="cancelDialog.show" max-width="400">
      <v-card>
        <v-card-title class="primary white--text py-4">
          {{ $vuetify.lang.t("$vuetify.maira_questionnaire.confirm_delete_question") }}
        </v-card-title>
        <v-card-text class="pa-4 mt-2 subtitle-1">
          {{ cancelDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" outlined @click="closeCancelDialog">
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>
          <v-btn color="primary" @click="confirmCancel">
            {{ $vuetify.lang.t("$vuetify.common.confirm_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from "vuex";
import {
  POST_MAIRA_QUESTIONNAIRE,
  UPDATE_MAIRA_QUESTIONNAIRE,
  ADD_ALERT,
  LOAD_EMAIL_CONFIGS
} from "@/store/_actiontypes";
import {
  MAIRA_QUESTIONNAIRE_INIT_DATA,
  ONE_DIRECTIONAL_CONFIG_INIT_DATA,
  DELIVERY_MODES
} from "@/global/_constants";
import _ from 'lodash';
import { ValidationObserver, ValidationProvider } from "vee-validate";
import "@/helpers/vee-validate";
import { extend } from 'vee-validate';
import validations from "@/helpers/validations";
import { isValidHhMmFormat, hhMmToMinutes } from "@/helpers/helper";
import { parseCheckboxOptions } from "@/helpers/checkboxParsing";


//Custom rule: disallow duplicate keys for the same question
extend('uniqueKey', {
  params: ['keys', 'currentIndex'],
  validate(value, { keys, currentIndex }) {
    if (!value) return true; // empty handled by "required"
    // Trim and compare with others except itself
    return !keys.some((k, i) => i !== currentIndex && k?.trim() === value?.trim());
  },

});


extend('uniqueArraySelection', {
  params: ['allSelections', 'currentIndex'],
  validate(value, { allSelections, currentIndex }) {
    if (!Array.isArray(value)) return true; 

    const normalized = value.map(v => v?.toString().trim().toLowerCase()).sort().join(',');

    return !allSelections.some((arr, idx) => {
      if (idx === currentIndex || !Array.isArray(arr)) return false;
      const compare = arr.map(v => v?.toString().trim().toLowerCase()).sort().join(',');
      return compare === normalized;
    });
  },
  message: 'Duplicate selection detected. Each combination must be unique.'
});


export default {
  name: 'CreateMairaQuestionnaire',
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit'].includes(value)
    },
    isQuestionnaireDialogVisible: { 
      type: Boolean, 
      default: false 
    }  
  },
  data() {
    return {
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",

      formData: _.cloneDeep(MAIRA_QUESTIONNAIRE_INIT_DATA()),
      deliveryModes: DELIVERY_MODES,
      availableSenders: [],
      isSendersLoading: false,
      scheduleOverlapError: null,
      recipientSearch: '',

      tempKeys: [[]],
      tempValues: [[]],
      logicErrors: [[]],
      isLoading: false,
      deleteDialog: {
        show: false,
        questionIndex: null,
        message: ''
      },
      cancelDialog: {
        show: false,
        message: this.$vuetify.lang.t("$vuetify.maira_questionnaire.confirm_alert_questionnaire")
      },
      questionIdCounter: 1,
    };
  },
  computed: {
    ...mapState({
      selectedMairaQuestionnaire: (state) => state.project.selectedMairaQuestionnaire,
      emailConfigs: (state) => state.project.emailConfigs,
    }),

    questionTypes() {
      return [
        { value: 'text', text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.text') },
        { value: 'number', text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.number') },
        { value: 'radio', text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.radio') },
        { value: 'checkbox', text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.checkbox') }
      ];
    },

    requiredOptions() {
      return [
        { text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.true'), value: true },
        { text: this.$vuetify.lang.t('$vuetify.maira_questionnaire.false'), value: false }
      ];
    },

    statusOptions() {
      return [
        { text: 'Active', value: 'true' },
        { text: 'Inactive', value: 'false' }
      ];
    },
    hasOptionErrors() {
      return this.formData.questions.some(question => question.optionError);
    },
    hasDuplicateOrderErrors() {
      return this.formData.questions.some(question => question.duplicateOrderError);
    },
    hasDuplicateKeyValueAcrossForm() {
      return this.formData.questions.some(q => this.hasDuplicateKeyValue(q));
    },
    isFormInvalid() {
      // Check ValidationProviders for key/value fields
      const keyErrorsExist = this.$refs.keyProviders?.some(provider => provider.errors.length > 0);
      const valueErrorsExist = this.$refs.valueProviders?.some(provider => provider.errors.length > 0);

      return keyErrorsExist || valueErrorsExist || this.hasOptionErrors || this.hasDuplicateOrderErrors || this.hasDuplicateKeyValueAcrossForm;
    },
    getQuestionsOrder() {
      return this.formData.questions.map(q => q.order);
    }
  },
  created() {
    extend('timeFormat', {
      validate(value) {
        return isValidHhMmFormat(value);
      },
      message: this.$vuetify.lang.t('$vuetify.maira_questionnaire.time_format_invalid_error')
    });

    extend('startEndNotEqual', {
      params: ['start'],
      validate(end, { start }) {
        return end !== start;
      },
      message: this.$vuetify.lang.t('$vuetify.maira_questionnaire.window_start_end_equal_error')
    });

    extend('validEmailList', {
      validate(value) {
        if (!value || (Array.isArray(value) && value.length === 0)) return false;

        const checkEmail = validations.email();
        if (Array.isArray(value)) {
          return value.every(item => checkEmail(item) === true);
        }
        
        return false;
      },
      message: this.$vuetify.lang.t('$vuetify.maira_questionnaire.recipients_email_invalid_error')
    });

    extend('activeSender', {
      params: ['senders'],
      validate(value, { senders }) {
        if (!Array.isArray(value) || value.length === 0) return false;

        if (!Array.isArray(senders) || senders.length === 0) return false;

        return value.some(selected =>
          senders.some(sender =>
            sender.value.email === selected.email &&
            sender.value.provider === selected.provider
          )
        );
      },
      message: this.$vuetify.lang.t('$vuetify.maira_questionnaire.senders_required')
    });

    extend('circularSchedule', {
      params: ['start', 'end'],
      validate(value, { start, end }) {
        const startM = hhMmToMinutes(start);
        const endM = hhMmToMinutes(end);
        const deliveryM = hhMmToMinutes(value);

        const MINUTES_IN_DAY = 24 * 60;
        const positiveModulo = (n, m) => ((n % m) + m) % m;

        const dEnd = positiveModulo(endM - startM, MINUTES_IN_DAY);
        const dDelivery = positiveModulo(deliveryM - startM, MINUTES_IN_DAY);

        if (dDelivery < dEnd) {
          return false;
        }

        return true;
      },
      message: this.$vuetify.lang.t('$vuetify.maira_questionnaire.circular_schedule_violation_error')
    });
  },
  watch: {
    selectedMairaQuestionnaire: {
      immediate: true,
      handler(newData) {
        if (this.mode === 'edit' && newData && newData.name) {
          this.populateFormData(newData);
        } else if (this.mode === 'create') {
          this.initializeCreateMode();
        }
      }
    },

    'formData.questions': {
      deep: true,
      handler(questions) {
        this.checkDuplicateOrders();
        // Validate options for radio/checkbox questions
        questions.forEach(question => {
          if (question.type === 'radio' || question.type === 'checkbox') {
            this.validateQuestionOptions(question);
          }
        });
      }
    },

    isQuestionnaireDialogVisible(newVal) {
      if (newVal) {
        this.fetchSenders();
        if (this.mode === 'create') this.initializeCreateMode();
      }
    },
  },

  mounted() {
    if (this.isQuestionnaireDialogVisible) {
      this.fetchSenders();
      if (this.mode === 'create') {
        this.initializeCreateMode();
      }
    } else {
      if (this.mode === 'create') {
        this.initializeCreateMode();
      }
    }
  },

  methods: {
    ...mapActions("project", [
      POST_MAIRA_QUESTIONNAIRE,
      UPDATE_MAIRA_QUESTIONNAIRE,
      LOAD_EMAIL_CONFIGS,
    ]),

    initializeCreateMode() {
      this.resetFormData();
      this.addQuestion();
    },

    showSnackBar(type = "success", message = "") {
      const color = type === "success" ? "success" : "error";
      this.$store.dispatch(
        `alert/${ADD_ALERT}`,
        {
          message,
          color,
        },
        { root: true }
      );
    },

    sanitizeSenders() {
      const selected = this.formData.one_directional_config.senders || [];
      const allowed = this.availableSenders || [];

      const cleaned = selected.filter(sel =>
        allowed.some(sender =>
          sender.value.email === sel.email &&
          sender.value.provider === sel.provider
        )
      );

      this.$set(this.formData.one_directional_config, 'senders', cleaned);
    },

    validateSchedules() {
      if (!this.formData.is_one_directional) return true;
      const mode = this.formData.one_directional_config.delivery_mode;
      if (mode !== this.deliveryModes.SCHEDULED) return true;
      const schedules = this.formData.one_directional_config.schedules || [];
      if (schedules.length === 0) {
        this.scheduleOverlapError = this.$vuetify.lang.t('$vuetify.maira_questionnaire.at_least_one_schedule_required');
        return false;
      }
      for (let i = 0; i < schedules.length; i++) {
        const s = schedules[i];
        if (!isValidHhMmFormat(s.window_start) || !isValidHhMmFormat(s.window_end) || !isValidHhMmFormat(s.delivery_time)) {
          this.scheduleOverlapError = this.$vuetify.lang.t('$vuetify.maira_questionnaire.schedule_time_invalid_error');
          return false;
        }
      }
      return true;
    },

    addRecipientFromSearch() {
      const val = (this.recipientSearch || '').toString().trim();
      if (!val) {
        this.recipientSearch = null;
        return;
      }

      const checkEmail = validations.email();
      const valid = checkEmail(val) === true;
      if (!valid) {
        this.showSnackBar('error', this.$vuetify.lang.t('$vuetify.maira_questionnaire.recipients_email_invalid_error'));
        return;
      }

      const recipients = this.formData.one_directional_config.recipients || [];
      if (!recipients.includes(val)) {
        recipients.push(val);
        this.$set(this.formData.one_directional_config, 'recipients', recipients);
      }

      this.recipientSearch = '';
    },

    handleRecipientBlur() {
      this.addRecipientFromSearch();
    },

    async fetchSenders() {
      this.isSendersLoading = true;
      const projectId = this.$route.query.pid;
      try {
        const response = await this.LOAD_EMAIL_CONFIGS({ projectId, getAll: true });
        this.availableSenders = response.data.items
          .filter(config => config.status === "active")
          .map(config => ({
            text: `${config.email} (${config.provider})`,
            value: { email: config.email, provider: config.provider }
          }));
      } catch (error) {
        this.showSnackBar(
          "error",
          `${this.$vuetify.lang.t("$vuetify.maira_questionnaire.load_sender_emails_error")} ${error?.message}`
        );
      } finally {
        this.isSendersLoading = false;
      }
    },

    addSchedule() {
      this.formData.one_directional_config.schedules.push({
        window_start: "",
        window_end: "",
        delivery_time: ""
      });
    },

    removeSchedule(index) {
      this.formData.one_directional_config.schedules.splice(index, 1);
    },

    checkScheduleOverlaps() {
      this.scheduleOverlapError = null;
      const schedules = this.formData.one_directional_config.schedules;
      
      if (!this.formData.is_one_directional || 
          this.formData.one_directional_config.delivery_mode !== this.deliveryModes.SCHEDULED) {
        return true;
      }

      if (!schedules || schedules.length < 2) return true;

      const intervals = [];

      // Step 1: Generate Intervals (handling overnight split)
      for (let i = 0; i < schedules.length; i++) {
        const schedule = schedules[i];

        const start = hhMmToMinutes(schedule.window_start);
        const end = hhMmToMinutes(schedule.window_end);

        if (start === null || end === null) continue;

        if (start < end) {
          intervals.push({ start, end, originalIndex: i });
        } else {
          // Overnight: split into [Start -> 24h] and [0 -> End]
          intervals.push({ start: start, end: 24 * 60, originalIndex: i });
          intervals.push({ start: 0, end: end, originalIndex: i });
        }
      };

      const overlapsMap = {};
      const n = intervals.length;

      // Step 2: Check for overlaps
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const int1 = intervals[i];
          const int2 = intervals[j];

          // Skip comparing parts of the same original schedule
          if (int1.originalIndex === int2.originalIndex) continue;

          // Check intersection: start1 < end2 AND start2 < end1
          if (int1.start < int2.end && int2.start < int1.end) {
            const idx1 = int1.originalIndex;
            const idx2 = int2.originalIndex;

            if (!overlapsMap[idx1]) overlapsMap[idx1] = new Set();
            if (!overlapsMap[idx2]) overlapsMap[idx2] = new Set();

            overlapsMap[idx1].add(idx2);
            overlapsMap[idx2].add(idx1);
          }
        }
      }

      // Step 3: Format Error Message
      const problemIndices = Object.keys(overlapsMap).map(Number).sort((a, b) => a - b);
      
      if (problemIndices.length > 0) {
        const reportedPairs = new Set();
        const errorDetails = [];

        const formatSchedule = (s) => `'${s.window_start}-${s.window_end}'`;

        problemIndices.forEach(idx => {
          const currentStr = formatSchedule(schedules[idx]);
          const conflicts = [];
          const relatedIndices = Array.from(overlapsMap[idx]).sort((a, b) => a - b);

          relatedIndices.forEach(relIdx => {
            // Create unique pair key to avoid duplicate reporting (e.g., A overlaps B, B overlaps A)
            const pair = [idx, relIdx].sort((a, b) => a - b).join('-');
            if (!reportedPairs.has(pair)) {
              conflicts.push(formatSchedule(schedules[relIdx]));
              reportedPairs.add(pair);
            }
          });

          if (conflicts.length > 0) {
            errorDetails.push(`${currentStr} ${this.$vuetify.lang.t("$vuetify.maira_questionnaire.overlaps_with")} [${conflicts.join(', ')}]`);
          }
        });

        if (errorDetails.length > 0) {
          this.scheduleOverlapError = errorDetails.join('; ');
          return false;
        }
      }

      return true;
    },

    addLogicR(questionIndex) {
      const q = this.formData.questions[questionIndex];
      if (!q.next_logic) this.$set(q, "next_logic", {});
      const key = "";
      this.$set(q.next_logic, key, "");
      this.tempKeys[questionIndex].push(key);
    },

    removeLogicR(questionIndex, key) {
      const q = this.formData.questions[questionIndex];
      if (q.next_logic && key in q.next_logic) {
        this.$delete(q.next_logic, key);
      }
      this.tempKeys[questionIndex] = Object.keys(q.next_logic);
    },

    renameLogicKey(questionIndex, idx) {
      const q = this.formData.questions[questionIndex];
      const prevKey = Object.keys(q.next_logic)[idx];
      const newKey = this.tempKeys[questionIndex][idx];

      if (prevKey === newKey) return;

      const value = q.next_logic[prevKey];
      this.$delete(q.next_logic, prevKey);
      this.$set(q.next_logic, newKey, value);
      this.tempKeys[questionIndex] = Object.keys(q.next_logic);
    },


    renameLogicKey1(questionIndex, idx) {
      const q = this.formData.questions[questionIndex];
      const newKey = this.tempKeys[questionIndex][idx];

      // Prevent rename if same key-value pair already exists
      const value = q.next_logic[newKey];
      const occurrences = Object.entries(q.next_logic).filter(
        ([k, v]) => k === newKey && v === value
      );

      if (occurrences.length > 1) {
        this.$set(this.tempKeys[questionIndex], idx, '');
        this.$toast?.error?.('Duplicate key-value pair not allowed');
        return;
      }

      // Rename normally (Vue 2 reactive)
      const oldKey = Object.keys(q.next_logic)[idx];
      this.$set(q.next_logic, newKey, q.next_logic[oldKey]);
      this.$delete(q.next_logic, oldKey);
    },

    populateFormData(questionnaireData) {
      const questions = questionnaireData.questions || {};
      const mainQuestions = questions.main || questions;

      const formattedQuestions = Object.values(mainQuestions)
        .filter(q => q && q.question)
        .map((q, index) => ({
          id: this.generateQuestionId(),
          question: q.question || "",
          type: q.type || "text",
          order: q.order || index + 1,
          required: q.required !== undefined ? q.required : true,
          placeholder: q.placeholder || "",
          options: Array.isArray(q.options) ? [...q.options] : [],
          duplicateOrderError: null,
          optionError: null,
          next_logic: q.next_logic || {}
        }));

      // Initialize tempKeys and tempValues for editing next_logic
      // Initialize tempKeys and tempValues for editing next_logic
      this.tempKeys = formattedQuestions.map((q, qIndex) => {
        if (q.next_logic && Object.keys(q.next_logic).length > 0) {
          if (q.type === 'checkbox') {
            // For checkbox, convert keys back to array of selected options
            return Object.keys(q.next_logic).map(key => parseCheckboxOptions(key, q.options));
          }
          return Object.keys(q.next_logic);
        }
        return [];
      });

      this.tempValues = formattedQuestions.map(q => {
        if (q.next_logic) return Object.values(q.next_logic);
        return [];
      });

      // Initialize logicErrors
      this.logicErrors = formattedQuestions.map(q => q.next_logic ? Object.keys(q.next_logic).map(() => "") : []);

      this.formData = {
        name: questionnaireData.name || "",
        questions: formattedQuestions,
        context_template: questionnaireData.context_template || "",
        active: questionnaireData.active?.toString() || "true",
        response_instructions: questionnaireData.response_instructions || "",
        is_one_directional: questionnaireData.is_one_directional || false,
        one_directional_config: _.merge(
          _.cloneDeep(ONE_DIRECTIONAL_CONFIG_INIT_DATA()),
          questionnaireData.one_directional_config || {}
        )
      };
    },


    generateQuestionId() {
      return `q_${Date.now()}_${this.questionIdCounter++}`;
    },

    validateQuestionOptions(question) {
      if (!question.options || question.options.length === 0) return;

      const hasDuplicate = question.options.some((option, index) =>
        question.options.findIndex(o =>
          o.trim().toLowerCase() === option.trim().toLowerCase()
        ) !== index
      );

      question.optionError = hasDuplicate ? this.$vuetify.lang.t("$vuetify.maira_questionnaire.duplicate_option") : null;
    },

    submitForm() {
      if (this.isSendersLoading) return;

      this.addRecipientFromSearch(); 

      this.sanitizeSenders();

      this.scheduleOverlapError = null;
      
      this.$refs.observer.validate().then(success => {
        const isScheduleValid = this.checkScheduleOverlaps();
        const areSchedulesPresent = this.validateSchedules();
        if (success && isScheduleValid && areSchedulesPresent && this.validateFormData()) {
          const formattedData = this.formatDataForAPI();
          const projectId = this.$route.query.pid;
          this.submitQuestionnaire(projectId, formattedData);
        } else {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.scheduleOverlapError 
                ? this.scheduleOverlapError
                : this.$vuetify.lang.t("$vuetify.gpt_profile.profile_validation_error"),
              color: "red",
            },
            { root: true }
          );
        }
      });
    },

    validateFormData() {
      if (this.formData.questions.length === 0) {
        return false;
      }
      for (let i = 0; i < this.formData.questions.length; i++) {
        const question = this.formData.questions[i];

        if (!question.question?.trim()) return false;
        if (question.order == null) return false;
        if (question.optionError) return false;

        if (question.type === 'radio' || question.type === 'checkbox') {
          if (!question.options?.length) {
            return false;
          }

          const hasEmptyOptions = question.options.some(option => !option?.trim());
          if (hasEmptyOptions) {
            return false;
          }

          if (question.optionError) {
            return false;
          }
        }
      }

      return true;
    },

    formatDataForAPI() {
      const formattedData = {
        name: this.formData.name,
        questions: this.formData.questions.map((question, index) => {
          const formatted = {
            id: `q${index + 1}`,
            question: question.question,
            type: question.type,
            order: question.order,
            required: question.required
          };

          if ((question.type === 'text' || question.type === 'number') && question.placeholder) {
            formatted.placeholder = question.placeholder;
          } else if (question.type === 'radio' || question.type === 'checkbox') {
            formatted.options = question.options;
          }
          // next_logic as a single object
          const keys = this.tempKeys[index] || [];
          const values = this.tempValues[index] || [];
          if (question.type === 'checkbox') {
            // Convert keys back to comma-separated strings for checkbox
            if (keys.length) {
              const nextLogic = {};
              keys.forEach((key, i) => {
                if (key) {
                  // `key` is already an array of distinct option labels from the multi-select combobox (v-model is bound `multiple`) —
                  // join them directly instead of collapsing to a string and re-splitting, which would corrupt any option label that itself contains a comma.
                  const formattedKey = Array.isArray(key)
                    ? key.map(k => String(k).trim()).join(', ')
                    : String(key).trim();
                  const formattedValue = values[i] ? values[i] : "";
                  nextLogic[formattedKey] = formattedValue;
                }
              });
              formatted.next_logic = nextLogic;
            }

          } else {
            if (keys.length) {
              const nextLogic = {};
              keys.forEach((key, i) => {
                if (key) nextLogic[key] = values[i] || "";
              });
              formatted.next_logic = nextLogic;
            }
          }

          return formatted;
        }),
        context_template: this.formData.context_template,
        active: this.formData.active,
        response_instructions: this.formData.response_instructions,
        is_one_directional: this.formData.is_one_directional,
      };

      // Conditionally add the one_directional_config
      if (this.formData.is_one_directional) {
        const config = _.cloneDeep(this.formData.one_directional_config);

        if (config.delivery_mode !== this.deliveryModes.SCHEDULED) {
          config.schedules = [];
        }

        formattedData.one_directional_config = config;
      }

      return formattedData;
    },

    submitQuestionnaire(projectId, formattedData) {
      this.isLoading = true;

      if (this.mode === 'edit') {
        this.UPDATE_MAIRA_QUESTIONNAIRE({
          project_id: projectId,
          questionnaire_id: this.selectedMairaQuestionnaire.id,
          questionnaire_body: formattedData
        })
          .then(response => {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t("$vuetify.maira_questionnaire.update_success"),
                color: "success",
              },
              { root: true }
            );
            this.$emit('questionnaire-updated', response);
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      } else {
        this.POST_MAIRA_QUESTIONNAIRE({
          project_id: projectId,
          questionnaire_body: formattedData
        })
          .then(response => {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: this.$vuetify.lang.t("$vuetify.maira_questionnaire.create_success"),
                color: "success",
              },
              { root: true }
            );
            this.$emit('questionnaire-created', response);
            this.isLoading = false;
          })
          .catch((error) => {
            this.isLoading = false;
            const errorMessage = error.response?.data?.detail?.error;
            this.$store.dispatch(
              `alert/${ADD_ALERT}`,
              {
                message: errorMessage || this.$vuetify.lang.t("$vuetify.maira_questionnaire.maira_questionnaire_error"),
                color: "error",
              },
              { root: true }
            );
          });
      }
    },

    addQuestion() {
      this.formData.questions.push({
        id: this.generateQuestionId(),
        question: "",
        type: "text",
        order: this.formData.questions.length + 1,
        required: true,
        placeholder: "",
        options: [],
        duplicateOrderError: null,
        optionError: null
      });
    },

    removeQuestion(questionIndex) {
      this.formData.questions.splice(questionIndex, 1);
    },
    allowOnlyDigits(e) {
      // Allow only 0–9
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
    handleTypeChange(questionIndex) {
      const question = this.formData.questions[questionIndex];

      // Preserve old behavior
      if (question.type === 'text' || question.type === 'number') {
        question.options = [];
        question.placeholder = '';
        question.optionError = null;
      } else if (question.type === 'radio' || question.type === 'checkbox') {
        question.options = [''];
        question.placeholder = '';
      }

      // Reset question flow fields safely
      this.$set(this.tempKeys, questionIndex, []);
      this.$set(this.tempValues, questionIndex, []);
      this.$set(this.logicErrors, questionIndex, []);

      // Also reset next_logic object for this question
      if (question.next_logic) {
        this.$set(question, 'next_logic', {});
      }
    },


    addOption(questionIndex) {
      const question = this.formData.questions[questionIndex];

      const lastOption = question.options[question.options.length - 1];
      if (!lastOption || !lastOption.trim()) {
        return;
      }

      question.options.push('');
    },

    removeOption(questionIndex, optionIndex) {
      const question = this.formData.questions[questionIndex];
      if (question.options.length <= 1) return; // prevent deleting the last option

      // Get and remove the selected option
      const removed = question.options.splice(optionIndex, 1)[0];

      // Clean up next_logic if this option existed there
      if (question.next_logic?.[removed]) {
        this.$delete(question.next_logic, removed);
      }

      // Clean up related tempKeys and tempValues
      const keys = this.tempKeys[questionIndex] || [];
      const values = this.tempValues[questionIndex] || [];
      const idx = keys.indexOf(removed);
      if (idx !== -1) {
        keys.splice(idx, 1);
        values.splice(idx, 1);
      }

      // Update reactive arrays
      this.$set(this.tempKeys, questionIndex, keys);
      this.$set(this.tempValues, questionIndex, values);
    },

    openDeleteQuestionDialog(questionIndex) {
      this.deleteDialog = {
        show: true,
        questionIndex: questionIndex,
        message: this.$vuetify.lang.t("$vuetify.maira_questionnaire.delete_question_confirm")
      };
    },

    closeDeleteDialog() {
      this.deleteDialog.show = false;
    },

    confirmDelete() {
      this.removeQuestion(this.deleteDialog.questionIndex);
      this.closeDeleteDialog();
    },

    cancelQuestionnaire() {
      this.cancelDialog.show = true;
    },

    confirmCancel() {
      this.cancelDialog.show = false;
      this.$emit('close');
    },

    closeCancelDialog() {
      this.cancelDialog.show = false;
    },

    resetFormData() {
      this.formData = _.cloneDeep(MAIRA_QUESTIONNAIRE_INIT_DATA());
      this.formData.active = "true";
      this.questionIdCounter = 1;
      this.tempKeys = [];
      this.tempValues = [];
      this.logicErrors = [];
    },
    checkDuplicateOrders() {
      const orderCount = {};
      // how many times each order appears
      this.formData.questions.forEach(q => {
        if (q.order != null) {
          orderCount[q.order] = (orderCount[q.order] || 0) + 1;
        }
      });
      // Mark duplicates
      this.formData.questions.forEach(q => {
        q.duplicateOrderError = orderCount[q.order] > 1
          ? this.$vuetify.lang.t("$vuetify.maira_questionnaire.order_duplicate_check_messege", { order: q.order })
          : null;
      });
    },
    updateNextLogicValue(_questionIndex, _key, _val) {

    },

    hasDuplicateKeyValue(question) {
    },

    addLogicRadio0(questionIndex) {
      const q = this.formData.questions[questionIndex];
      if (!q.next_logic) this.$set(q, "next_logic", {});
      const key = "";
      this.$set(q.next_logic, key, "");
      this.tempKeys[questionIndex].push(key);
    },

    addLogicRadio(questionIndex) {
      // --- Ensure all base arrays exist ---
      if (!this.tempKeys[questionIndex]) this.$set(this.tempKeys, questionIndex, []);
      if (!this.tempValues[questionIndex]) this.$set(this.tempValues, questionIndex, []);
      if (!this.logicErrors[questionIndex]) {
        this.$set(this.logicErrors, questionIndex, {
          keyErrors: [],
          valueErrors: [],
        });
      }

      const keys = this.tempKeys[questionIndex];
      const values = this.tempValues[questionIndex];
      const logic = this.logicErrors[questionIndex];

      // Always safely initialize subarrays
      if (!logic.keyErrors) this.$set(logic, 'keyErrors', []);
      if (!logic.valueErrors) this.$set(logic, 'valueErrors', []);

      const keyErrors = logic.keyErrors;
      const valueErrors = logic.valueErrors;

      // --- Validate last mapping before adding new one ---
      if (keys.length > 0) {
        const lastIdx = keys.length - 1;
        const lastKey = keys[lastIdx];
        const lastValue = values[lastIdx];

        // Clear previous errors
        this.$set(keyErrors, lastIdx, "");
        this.$set(valueErrors, lastIdx, "");

        if (!lastKey || !String(lastKey).trim()) {
          this.$set(keyErrors, lastIdx, "Answer (key) is required before adding new mapping.");
          return;
        }

        if (!lastValue || !String(lastValue).trim()) {
          this.$set(valueErrors, lastIdx, "Next question (value) is required before adding new mapping.");
          return;
        }
      }

      // --- Add new empty mapping ---
      keys.push("");
      values.push("");
      keyErrors.push("");
      valueErrors.push("");

      // Ensure reactivity
      this.$set(this.tempKeys, questionIndex, keys);
      this.$set(this.tempValues, questionIndex, values);
      this.$set(this.logicErrors, questionIndex, { keyErrors, valueErrors });
    },

    addLogic(questionIndex) {
      const keys = this.tempKeys[questionIndex] || [];
      const values = this.tempValues[questionIndex] || [];
      const errors = this.logicErrors[questionIndex] || [];

      // Check last key before adding a new row
      if (keys.length > 0) {
        const lastKey = keys[keys.length - 1];
        if (!lastKey || !lastKey.trim()) {
          this.$set(errors, errors.length - 1, { key: "Key is required before adding new mapping.", value: "" });
          this.$set(this.logicErrors, questionIndex, errors);
          return;
        }

        // Check last value only when adding new mapping
        const lastValue = values[values.length - 1];
        if (!lastValue || String(lastValue).trim() === "") {
          this.$set(errors, errors.length - 1, {
            key: errors[errors.length - 1]?.key || "",
            value: "Value is required before adding new mapping."
          });
          this.$set(this.logicErrors, questionIndex, errors);
          return;
        }
      }

      const q = this.formData.questions[questionIndex];
      if (!q.next_logic) this.$set(q, "next_logic", {});

      // push empty row
      this.$set(this.tempKeys, questionIndex, [...keys, ""]);
      this.$set(this.tempValues, questionIndex, [...values, ""]);
      this.$set(this.logicErrors, questionIndex, [...errors, { key: "", value: "" }]);
    },

    removeLogicOLD(questionIndex, key) {
      const idx = this.tempKeys[questionIndex].indexOf(key);
      if (idx !== -1) {
        this.tempKeys[questionIndex].splice(idx, 1);
        this.tempValues[questionIndex].splice(idx, 1);
        this.logicErrors[questionIndex].splice(idx, 1);
      }

      const q = this.formData.questions[questionIndex];
      this.$delete(q.next_logic, key);
    },

    removeLogic(questionIndex, idx) {
      const keys = this.tempKeys[questionIndex] || [];
      const values = this.tempValues[questionIndex] || [];
      let errors = this.logicErrors[questionIndex] || [];

      const key = keys[idx];

      // Remove key/value
      keys.splice(idx, 1);
      values.splice(idx, 1);

      // Remove errors safely
      if (Array.isArray(errors)) {
        errors.splice(idx, 1);
      } else {
        errors = [];
      }

      // --- IMPORTANT: Use $set to trigger reactivity ---
      this.$set(this.tempKeys, questionIndex, keys);
      this.$set(this.tempValues, questionIndex, values);
      this.$set(this.logicErrors, questionIndex, errors);

      // Remove from next_logic object
      const q = this.formData.questions[questionIndex];
      if (q.next_logic && key in q.next_logic) {
        this.$delete(q.next_logic, key);
      }

      // --- OPTIONAL: trigger top-level reactivity ---
      this.$set(this.logicErrors, questionIndex, [...errors]);
    },


    updateNextLogic(questionIndex, idx) {
      const key = this.tempKeys[questionIndex][idx] || "";
      const value = this.tempValues[questionIndex][idx] || "";
      const q = this.formData.questions[questionIndex];

      if (!q.next_logic) this.$set(q, "next_logic", {});

      // Clean unused keys
      Object.keys(q.next_logic).forEach(existingKey => {
        if (!this.tempKeys[questionIndex].includes(existingKey)) {
          this.$delete(q.next_logic, existingKey);
        }
      });

      // Only set next_logic if both key & value exist
      if (key && value) this.$set(q.next_logic, key, value);
      else if (key && !value) this.$delete(q.next_logic, key);

      // --- Cross-field instant validation ---
      if (q.type === 'text' || q.type === 'number') {
        if (!this.logicErrors[questionIndex]) this.$set(this.logicErrors, questionIndex, []);
        const errors = this.logicErrors[questionIndex];

        // Ensure object exists for this row
        if (!errors[idx]) this.$set(errors, idx, { key: "", value: "" });

        // Key validation
        // errors[idx].key = key.trim() ? "" : "Key is required";
        // Value validation
        errors[idx].value =
          (typeof value === "string" ? value.trim() : value?.toString()?.trim())
            ? ""
            : "Value is required";

        this.$set(this.logicErrors, questionIndex, errors);
      } else if (q.type === 'radio' || q.type === 'checkbox') {
        if (!this.logicErrors[questionIndex]) {
          this.$set(this.logicErrors, questionIndex, { keyErrors: [], valueErrors: [] });
        }
        const logic = this.logicErrors[questionIndex];
        if (!logic.keyErrors[idx]) this.$set(logic.keyErrors, idx, "");
        if (!logic.valueErrors[idx]) this.$set(logic.valueErrors, idx, "");

        // logic.keyErrors[idx] = key.trim() ? "" : "Answer (key) is required";
        // logic.valueErrors[idx] = (typeof value === "string" ? value.trim() : value?.toString()?.trim())
        //     ? ""
        //     : "Value is required";
        this.$set(this.logicErrors, questionIndex, logic);
      }
    },


    validateLogicRowKeyOnly(questionIndex, idx) {
      const key = this.tempKeys[questionIndex][idx] || "";
      const errors = this.logicErrors[questionIndex];

      let keyError = "";
      if (!key || !String(key).trim()) keyError = "Key is required.";
      else {
        const duplicateKeys = this.tempKeys[questionIndex].filter(k => k === key);
        if (duplicateKeys.length > 1) keyError = "Duplicate key found.";
      }

      // Only set key error, leave value untouched
      const valueError = errors[idx]?.value || "";
      this.$set(errors, idx, { key: keyError, value: valueError });
    },

    getNextQuestionOptions(questionIndex) {
      const currentOrder = this.formData.questions[questionIndex].order;

      return [
        ...this.formData.questions
          .map(q => q.order)
          .filter(order => order > currentOrder), // only greater than current
        'end' // always allow 'end'
      ];
    },
    getAvailableOptions(questionIndex, idx, options) {
      const selectedKeys = this.tempKeys[questionIndex].filter((k, i) => i !== idx);
      return options.filter(opt => !selectedKeys.includes(opt));
    },
    validateLogicField(questionIndex, idx, fieldType) {
      const errors = this.logicErrors[questionIndex] || [];
      const keys = this.tempKeys[questionIndex] || [];
      const values = this.tempValues[questionIndex] || [];

      const key = keys[idx];
      const value = values[idx];

      // Initialize error object if missing
      if (!errors[idx]) this.$set(errors, idx, { key: "", value: "" });

      if (fieldType === "key") {
        // If key is empty, show error
        errors[idx].key = !key || !key.trim() ? "Key is required." : "";
        // Also validate value instantly
        errors[idx].value = !value || String(value).trim() === "" ? "Value is required." : "";
      } else if (fieldType === "value") {
        // If value is empty, show error
        errors[idx].value = !value || String(value).trim() === "" ? "Value is required." : "";
        // Also validate key instantly
        errors[idx].key = !key || !key.trim() ? "Key is required." : "";
      }

      this.$set(this.logicErrors, questionIndex, errors);
    },
    getSpecialAnswerOptions(questionType, questionIndex, currentIdx) {
      const specialMapping = questionType === 'text' ? 'any_text' : 'any_number';
      const keys = this.tempKeys[questionIndex];

      const alreadyUsed = keys.includes(specialMapping);
      const isCurrent = keys[currentIdx] === specialMapping;

      return alreadyUsed && !isCurrent ? [] : [specialMapping];
    },
    toggleValue(questionIndex, idx, value) {
      const current = this.tempKeys[questionIndex][idx];
      this.$set(this.tempKeys[questionIndex], idx, current === value ? '' : value);
      this.updateNextLogic(questionIndex, idx);
    },
    preventSpecialValueEdit(e, questionIndex, idx) {
      const currentValue = this.tempKeys[questionIndex][idx];
      const specialValues = ['any_number', 'any_text'];
      
      // If current value is a special value, prevent all keyboard edits
      if (specialValues.includes(currentValue)) {
          e.preventDefault();
      }
    },
    allowOnlyNumbersOrSpecial(e, currentValue) {
      if (currentValue === 'any_number' || !/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
    onComboboxSearchInput(searchValue, questionIndex, idx) {
      this.$set(this.tempKeys[questionIndex], idx, searchValue);
    },
  }
}
</script>

<style scoped>
.form-container {
  max-width: 600px;
}

.form-field {
  font-size: 18px;
  margin-bottom: 15px;
}

.question-card {
  border: 1px solid #e0e0e0;
}

.option-row {
  margin-bottom: 8px;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-field {
  font-size: 18px;
}

.add-question-btn {
  width: 100%;
  margin-bottom: 16px;
}

.form-actions {
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

/* .cancel-btn:hover {
  background-color: #fff2f2 !important;
} */

.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}

.custom-combobox >>> .v-select__selections {
  padding: 10px 10px !important;
}
.custom-combobox >>> .v-chip {
  margin: 4px;              
}
</style>